/**
 * Agent Service
 * AIDLC Administrative Backend
 *
 * API calls for agent CRUD operations.
 * Maps to endpoints defined in the Architecture Document, Section 04.
 */

import apiClient from './api';
import type { Agent, AgentStatusUpdate, AgentWorkspaceUpdate } from '../types/agent';

const AGENTS_PATH = '/agents';

/**
 * Get all agents with current status
 */
export async function getAllAgents(): Promise<Agent[]> {
  const response = await apiClient.get<Agent[]>(AGENTS_PATH);
  return response.data;
}

/**
 * Get a single agent by ID
 */
export async function getAgentById(agentId: string): Promise<Agent> {
  const response = await apiClient.get<Agent>(`${AGENTS_PATH}/${agentId}`);
  return response.data;
}

/**
 * Update an agent's status
 */
export async function updateAgentStatus(
  agentId: string,
  update: AgentStatusUpdate
): Promise<Agent> {
  const response = await apiClient.put<Agent>(
    `${AGENTS_PATH}/${agentId}/status`,
    update
  );
  return response.data;
}

/**
 * Update an agent's workspace (notes and tasks)
 */
export async function updateAgentWorkspace(
  agentId: string,
  update: AgentWorkspaceUpdate
): Promise<Agent> {
  const response = await apiClient.put<Agent>(
    `${AGENTS_PATH}/${agentId}/workspace`,
    update
  );
  return response.data;
}

/**
 * Get agent work history across sessions
 */
export async function getAgentHistory(
  agentId: string
): Promise<{ sessions: unknown[]; totalTokens: number }> {
  const response = await apiClient.get(`${AGENTS_PATH}/${agentId}/history`);
  return response.data;
}

/**
 * Get agent instruction .md file content
 */
export async function getAgentInstructions(
  agentId: string
): Promise<{ agentId: string; content: string; exists: boolean; lastModified: string | null; size: number }> {
  const response = await apiClient.get(`${AGENTS_PATH}/${agentId}/instructions`);
  return response.data;
}

/**
 * Update agent instruction .md file content
 */
export async function updateAgentInstructions(
  agentId: string,
  content: string
): Promise<{ agentId: string; content: string; exists: boolean; lastModified: string; size: number }> {
  const response = await apiClient.put(`${AGENTS_PATH}/${agentId}/instructions`, { content });
  return response.data;
}

/**
 * Get agent activity feed (active task + output lines)
 */
export interface ActivityLine {
  lineNumber: number;
  line: string;
  lineType: 'stdout' | 'stderr' | 'system';
  timestamp: string;
}

export interface ActiveTask {
  taskId: string;
  agentId: string;
  source: 'chat' | 'go-do-it' | 'task-tool' | 'manual';
  subject: string;
  startedAt: string;
}

export interface AgentActivityResponse {
  agentId: string;
  activeTask: ActiveTask | null;
  lines: ActivityLine[];
  count: number;
}

/**
 * Kill an agent's active session
 */
export async function killAgentSession(agentId: string): Promise<{ ok: boolean; message: string }> {
  const response = await apiClient.post<{ ok: boolean; message: string }>(`${AGENTS_PATH}/${agentId}/kill-session`);
  return response.data;
}

/**
 * Get all active tasks across all agents
 */
export async function getAllActiveTasks(): Promise<{ tasks: ActiveTask[] }> {
  const response = await apiClient.get<{ tasks: ActiveTask[] }>(`${AGENTS_PATH}/active-tasks`);
  return response.data;
}

export async function getAgentActivity(agentId: string): Promise<AgentActivityResponse> {
  const response = await apiClient.get<AgentActivityResponse>(`${AGENTS_PATH}/${agentId}/activity`);
  return response.data;
}

/**
 * Get global governance document
 */
export async function getGlobalGovernance(): Promise<{ content: string; exists: boolean; lastModified: string | null; size: number }> {
  const response = await apiClient.get(`${AGENTS_PATH}/governance/global`);
  return response.data;
}

/**
 * Update global governance document
 */
export async function updateGlobalGovernance(content: string): Promise<{ content: string; exists: boolean; lastModified: string; size: number }> {
  const response = await apiClient.put(`${AGENTS_PATH}/governance/global`, { content });
  return response.data;
}
